<template>
  <div class="editable-user-detail">
    <form
      method="post"
      data-test="my-account-details-form"
      @submit.prevent="submitForm"
      class="account-details-form"
    >
      <div class="account-text-input">
        <label for="first-name">First name</label>
        <div
          v-if="v$.updatedAccountDetails.firstName.required.$invalid && submitStatus === 'Error'"
          class="text-danger"
          data-test="first-name-required"
        >
          Required field
        </div>
        <div
          v-if="v$.updatedAccountDetails.firstName.maxLength.$invalid && submitStatus === 'Error'"
          class="text-danger"
          data-test="first-name-max"
        >
          First name must be less than 40 characters long.
        </div>
        <input
          v-model.trim="updatedAccountDetails.firstName"
          type="text"
          id="first-name"
          :disabled="!isEditing"
          class="text-input"
          @focus="removeErrorMessage"
          data-test="first-name-input"
        />
        <i class="placeholder-icon"
          ><img src="../../assets/registration/user-icon.svg" alt="User icon"
        /></i>
      </div>
      <div class="account-text-input">
        <label for="last-name">Last name</label>
        <div
          v-if="v$.updatedAccountDetails.lastName.required.$invalid && submitStatus === 'Error'"
          class="text-danger"
          data-test="last-name-required"
        >
          Required field
        </div>
        <div
          v-if="v$.updatedAccountDetails.lastName.maxLength.$invalid && submitStatus === 'Error'"
          class="text-danger"
          data-test="last-name-max"
        >
          Last name must be less than 40 characters long.
        </div>
        <input
          v-model.trim="updatedAccountDetails.lastName"
          type="text"
          id="last-name"
          :disabled="!isEditing"
          class="text-input"
          @focus="removeErrorMessage"
          data-test="last-name-input"
        />
        <i class="placeholder-icon"
          ><img src="../../assets/registration/user-icon.svg" alt="User icon"
        /></i>
      </div>
      <div class="account-text-input">
        <label for="email">Email</label>
        <div
          v-if="v$.updatedAccountDetails.email.required.$invalid && submitStatus === 'Error'"
          class="text-danger"
          data-test="email-required"
        >
          Required field
        </div>
        <div
          v-if="v$.updatedAccountDetails.email.email.$invalid && submitStatus === 'Error'"
          class="text-danger"
          data-test="email-invalid"
        >
          Invalid email address
        </div>
        <div
          v-if="serverError && submitStatus === 'Error'"
          class="text-danger"
          data-test="email-error"
        >
          {{ serverError }}
        </div>
        <input
          v-model.trim="updatedAccountDetails.email"
          id="email"
          :disabled="!isEditing"
          class="text-input"
          @focus="removeErrorMessage"
          data-test="email-input"
        />
        <i class="placeholder-icon"
          ><img alt="Email icon" src="../../assets/registration/email-icon.svg"
        /></i>
      </div>
      <button
        v-if="!isEditing"
        class="btn btn-default-edit"
        @click.prevent="startEditing"
        data-test="edit-button"
        type="button"
      >
        Edit Details
      </button>
      <div v-else class="btn-container">
        <SubmitButton
          data-test="submit-details"
          :buttonClass="'btn-primary'"
          :isSubmitted="isSubmitted"
          :useRecaptcha="false"
          :disabled="attemptedToSubmit"
          style="min-width: 120px"
          >Save Changes</SubmitButton
        >
        <button
          class="btn btn-default"
          @click="cancelEditing"
          data-test="cancel-editing"
          type="button"
        >
          Cancel
        </button>
      </div>
    </form>
  </div>
</template>
<script setup lang="ts">
import { computed, ref } from "vue";
import axios, { AxiosError } from "axios";
import { useVuelidate } from "@vuelidate/core";
import { email, maxLength, required } from "@vuelidate/validators";
import SubmitButton from "../submit-button.vue";
import { MyAccountDetailsApiModel } from "../../api-models";

const props = defineProps<{
  editableDetails: MyAccountDetailsApiModel;
}>();

const emit = defineEmits(["toggle-success-message"]);

const updatedAccountDetails = ref<MyAccountDetailsApiModel>({
  firstName: props.editableDetails.firstName,
  lastName: props.editableDetails.lastName,
  email: props.editableDetails.email,
});

const rules = {
  updatedAccountDetails: {
    firstName: {
      required,
      maxLength: maxLength(40),
    },
    lastName: {
      required,
      maxLength: maxLength(40),
    },
    email: {
      email,
      required,
    },
  },
};

const v$ = useVuelidate(rules, { updatedAccountDetails }, { $autoDirty: true });

const isEditing = ref(false);
const serverError = ref<string | null>(null);
const attemptedToSubmit = ref(false);
const submitStatus = ref("");

const isSubmitted = computed(() => submitStatus.value === "OK");

function removeErrorMessage(): void {
  submitStatus.value = "";
}

async function successfulDetailsUpdate(): Promise<void> {
  isEditing.value = false;
  emit("toggle-success-message", {
    isDetailsUpdated: true,
    detailChanged: "details",
    updatedDetails: updatedAccountDetails.value,
  });
  submitStatus.value = "";
}

async function submitDetails(): Promise<void> {
  await axios
    .post("/api/myaccount/update", updatedAccountDetails.value)
    .then((resp) => {
      if (resp.status === 200) {
        successfulDetailsUpdate();
      }
    })
    .catch((e: AxiosError) => {
      submitStatus.value = "Error";
      const statusCode = e.response?.status;
      if (statusCode === 404) {
        serverError.value = e.response?.data;
      }
    });
  attemptedToSubmit.value = false;
}

async function submitForm(): Promise<void> {
  const isFormValid = await v$.value.$validate();
  attemptedToSubmit.value = true;
  if (!isFormValid) {
    submitStatus.value = "Error";
    attemptedToSubmit.value = false;
  } else {
    submitStatus.value = "OK";
    submitDetails();
  }
}

function resetDetails(): void {
  v$.value.$reset();
  updatedAccountDetails.value.firstName = props.editableDetails.firstName;
  updatedAccountDetails.value.lastName = props.editableDetails.lastName;
  updatedAccountDetails.value.email = props.editableDetails.email;
}

function startEditing(): void {
  isEditing.value = true;
}

function cancelEditing(): void {
  removeErrorMessage();
  isEditing.value = false;
  resetDetails();
}
</script>
<style lang="less" scoped>
@import (reference) "../../styles/bootstrap/bootstrap.less";
@import (reference) "../../styles/bootstrap/variables.less";

.account-details-form {
  padding-inline: 1rem;
  min-height: 260px;
  margin-bottom: 1rem;

  label,
  input {
    font-weight: normal;
    color: @form-text-color;
  }
}

.account-text-input {
  position: relative;
  display: flex;
  flex-direction: column;
  width: 80%;
  margin-bottom: 1rem;
  @media (max-width: @screen-sm) {
    width: 100%;
  }
}

.text-input {
  border: none;
  border-bottom: 1px solid rgba(@form-text-color, 0.5);
  background-color: @registration-background-color;
  height: 35px;
  max-width: 100%;
  padding-left: 30px;

  &:focus,
  &:active {
    outline: none;
    border-bottom: 1px solid @form-text-color;
  }

  &:disabled {
    background-color: transparent;
  }
}

.placeholder-icon {
  position: absolute;
  bottom: 10px;
  left: 10px;

  img {
    height: 14px;
  }
}

.btn-default-edit {
  margin-top: 2rem;
}

.btn-container {
  display: flex;
  justify-content: flex-start;
  gap: 2rem;
  margin-top: 2rem;
}
</style>
